/** Exports plan (Excel/CSV, Word/HTML, PDF via impression). */

import type { StrategicPlanRow } from '@isbd/shared';
import { PLAN_ROW_STATUSES } from '@isbd/shared';

function statusLabel(value: string): string {
  return PLAN_ROW_STATUSES.find((s) => s.value === value)?.label ?? value ?? '—';
}

function escapeHtml(s: string): string {
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

function downloadBlob(filename: string, blob: Blob) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

export function exportPlanCsv(_title: string, rows: StrategicPlanRow[], filename: string) {
  const header = ['Axe', 'Objectif', 'Actions prioritaires', 'Responsable', 'Échéance', 'Statut'];
  const lines = [
    header.join(';'),
    ...rows.map((r) =>
      [r.axe, r.objectif, r.actions, r.responsable, r.echeance, statusLabel(r.statut)]
        .map((c) => `"${String(c ?? '').replace(/"/g, '""')}"`)
        .join(';'),
    ),
  ];
  const bom = '\uFEFF';
  downloadBlob(
    filename.endsWith('.csv') ? filename : `${filename}.csv`,
    new Blob([bom + lines.join('\n')], { type: 'text/csv;charset=utf-8' }),
  );
}

export function exportPlanWord(title: string, rows: StrategicPlanRow[], filename: string) {
  const tableRows = rows
    .map(
      (r) => `<tr>
      <td>${escapeHtml(r.axe)}</td>
      <td>${escapeHtml(r.objectif)}</td>
      <td>${escapeHtml(r.actions)}</td>
      <td>${escapeHtml(r.responsable)}</td>
      <td>${escapeHtml(r.echeance)}</td>
      <td>${escapeHtml(statusLabel(r.statut))}</td>
    </tr>`,
    )
    .join('');
  const html = `<!DOCTYPE html><html><head><meta charset="utf-8"><title>${escapeHtml(title)}</title>
  <style>
    body{font-family:Calibri,Arial,sans-serif;font-size:11pt}
    h1{font-size:16pt}
    table{border-collapse:collapse;width:100%}
    th,td{border:1px solid #333;padding:6px;vertical-align:top}
    th{background:#f3d9c5}
  </style></head><body>
  <h1>${escapeHtml(title)}</h1>
  <table>
    <thead><tr>
      <th>Axe</th><th>Objectif</th><th>Actions prioritaires</th>
      <th>Responsable</th><th>Échéance</th><th>Statut</th>
    </tr></thead>
    <tbody>${tableRows}</tbody>
  </table>
  </body></html>`;
  downloadBlob(
    filename.endsWith('.doc') ? filename : `${filename}.doc`,
    new Blob([html], { type: 'application/msword' }),
  );
}

export function exportPlanPdf(title: string, rows: StrategicPlanRow[]) {
  const tableRows = rows
    .map(
      (r) => `<tr>
      <td>${escapeHtml(r.axe)}</td>
      <td>${escapeHtml(r.objectif)}</td>
      <td>${escapeHtml(r.actions)}</td>
      <td>${escapeHtml(r.responsable)}</td>
      <td>${escapeHtml(r.echeance)}</td>
      <td>${escapeHtml(statusLabel(r.statut))}</td>
    </tr>`,
    )
    .join('');
  const w = window.open('', '_blank');
  if (!w) return;
  w.document.write(`<!DOCTYPE html><html><head><meta charset="utf-8"><title>${escapeHtml(title)}</title>
  <style>
    body{font-family:Georgia,serif;padding:1.5rem;color:#111}
    h1{font-size:1.25rem}
    table{border-collapse:collapse;width:100%;font-size:0.85rem}
    th,td{border:1px solid #444;padding:0.4rem;vertical-align:top}
    th{background:#eee}
    @media print{button{display:none}}
  </style></head><body>
  <button onclick="window.print()">Imprimer / Enregistrer en PDF</button>
  <h1>${escapeHtml(title)}</h1>
  <table>
    <thead><tr>
      <th>Axe</th><th>Objectif</th><th>Actions prioritaires</th>
      <th>Responsable</th><th>Échéance</th><th>Statut</th>
    </tr></thead>
    <tbody>${tableRows}</tbody>
  </table>
  </body></html>`);
  w.document.close();
}
